import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Search, Star, Users } from 'lucide-react'
import PageHero from '../shared/PageHero'
import { contentApi, PackageItem } from '../../services/contentApi'
import { getImageUrl, IMAGE_CONFIG } from '../../config/settings'

const PackagesPage = () => {
  const [packages, setPackages] = useState<PackageItem[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  const load = (term?: string) => {
    setLoading(true)
    contentApi.getPackages(1, term)
      .then((res) => setPackages(res.data))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <PageHero
        eyebrow="Yatra packages"
        title="Curated packages near Manauna Dham"
        subtitle="Multi-day yatra plans, group packages, and vendor-led experiences around Aonla and Bareilly."
      >
        <form
          className="max-w-xl flex gap-2"
          onSubmit={(e) => { e.preventDefault(); load(search.trim() || undefined) }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search packages..."
              className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <button type="submit" className="px-5 py-3 rounded-xl bg-primary-500 font-semibold hover:bg-primary-600 transition-colors">
            Search
          </button>
        </form>
      </PageHero>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{[1, 2, 3].map((i) => <div key={i} className="h-80 rounded-2xl bg-gray-200 animate-pulse" />)}</div>
        ) : packages.length === 0 ? (
          <div className="text-center py-16 rounded-2xl bg-white border border-gray-200">
            <p className="text-gray-600">No packages listed yet. Check back soon or browse <Link to="/rooms" className="text-primary-600 font-medium">rooms near Manauna</Link>.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {packages.map((pkg) => (
              <article key={pkg.id} className="group rounded-2xl overflow-hidden bg-white border border-gray-200/80 shadow-sm hover:shadow-xl transition-all">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={pkg.featured_img ? getImageUrl(pkg.featured_img) : IMAGE_CONFIG.FALLBACK_IMAGE}
                    alt={pkg.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="font-bold text-lg text-gray-900 leading-snug">{pkg.title}</h2>
                    {pkg.avg_rating > 0 && (
                      <span className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold">
                        <Star className="h-4 w-4 fill-primary-500 text-primary-500" /> {pkg.avg_rating.toFixed(1)}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">{pkg.description}</p>
                  <div className="mt-4 flex flex-wrap gap-3 text-xs text-gray-500">
                    {pkg.number_of_days ? <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {pkg.number_of_days} days</span> : null}
                    {pkg.max_persons ? <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Up to {pkg.max_persons}</span> : null}
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-xl font-bold text-gray-900">₹{pkg.price.toLocaleString('en-IN')}</p>
                    {pkg.vendor_username && (
                      <Link to={`/vendors/${pkg.vendor_username}`} className="text-xs font-medium text-primary-600 hover:underline">
                        by {pkg.vendor_username}
                      </Link>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

export default PackagesPage
